import { Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { digger } from '@cbre/digger';
import { Column, FormControlOptions } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
import { _ExistingControlSymbol } from '@controls/utils/control-symbols';
import { FormControlMaker } from '@controls/model/form-control-maker';

/**
 * This service finds or generate and adds a FormControl to the given form based on a given Column configuration and Data
 * @see InputControlBuilder in [1. Getting Started - The Responsibility Interfaces & Segregation](https://confluence.cbre.com/pages/viewpage.action?pageId=106119767)
 */
@Injectable({ providedIn: 'root' })
export class InputControlBuilder implements FormControlMaker<Column> {
  constructor(protected fb: UntypedFormBuilder) {}

  /**
   * Build and register a new form control to context form for a given column
   * @param col
   * @param formGroup
   */
  public makeControlEntry(col: Column, form: UntypedFormGroup, data: DataObject): AbstractControl {
    const { formControlOptions } = col;
    let control: AbstractControl;
    if (formControlOptions.parentControl != null) {
      control = this._buildControlFromParentOpts(col, form, data);
    } else {
      control = this.buildAndRegisterColumnControl(col, form, data);
    }
    return control;
  }

  protected _buildControlFromParentOpts(col: Column, form: UntypedFormGroup, data: DataObject): AbstractControl {
    const { formControlOptions } = col;
    const { parentControl } = formControlOptions;

    const lastControl = this._makeParentControl(form, parentControl, data);
    return this.buildAndRegisterColumnControl(col, lastControl as UntypedFormGroup, data);
  }

  protected _makeParentControl = (
    targetFormGroup: AbstractControl,
    parentControlOpts: FormControlOptions<any>,
    data?: DataObject
  ): AbstractControl => {
    const { type } = parentControlOpts;
    return type === 'array'
      ? this._makeArrayControl(targetFormGroup as UntypedFormGroup, parentControlOpts, data)
      : this._makeNestedControl(targetFormGroup as UntypedFormGroup, parentControlOpts, data);
  };

  protected _makeArrayControl = (form: UntypedFormGroup, controlOpts: FormControlOptions<any>, data?: DataObject) => {
    const { name, children, controlsConfig = {} } = controlOpts;
    let arrayCtrl = form.get(name) as UntypedFormArray;
    if (!arrayCtrl) {
      const entries: any[] = data ? data[name] : [];
      // create target form array
      arrayCtrl = this.fb.array([]);
      (entries || []).forEach(entry => {
        const entryGroup = this.fb.group(controlsConfig);
        entryGroup.patchValue(entry);
        arrayCtrl.push(entryGroup);
      });
      if (form.registerControl) {
        form.registerControl(name, arrayCtrl);
      }
    }
    let group = arrayCtrl.at(0);
    if (!group) {
      group = this.fb.group({});
      arrayCtrl.push(group);
    }
    if (children != null) {
      return this._makeChildControl(children, group);
    }
    return group;
  };

  protected _makeNestedControl = (form: UntypedFormGroup, controlOpts: FormControlOptions<any>, data?: DataObject) => {
    const { name, children, controlsConfig = {} } = controlOpts;
    let formGroup = form.get(name) as UntypedFormGroup;
    if (!formGroup) {
      formGroup = this.fb.group(controlsConfig);
      if (data) {
        const nextData = data[name] || {};
        formGroup.patchValue(nextData);
      }
      if (form.registerControl) {
        form.registerControl(name, formGroup);
      }
    } else {
      this._ensureControlsConfig(formGroup, Object.entries(controlsConfig));
    }
    if (children != null) {
      return this._makeChildControl(children, formGroup);
    }
    return formGroup;
  };

  private _ensureControlsConfig(formGroup: UntypedFormGroup, controlConfigs: [string, any[]][]) {
    controlConfigs
      .filter(([controlName]) => formGroup.get(controlName) == null)
      .forEach(([controlName, controlConfig]) => {
        const [value, validators] = controlConfig;
        const control = this.fb.control(value, validators);
        formGroup.registerControl(controlName, control);
      });
  }

  protected _makeChildControl(children: { [key: string]: FormControlOptions<any> }, formGroup: AbstractControl) {
    const [child] = Object.values(children);
    return this._makeParentControl(formGroup, child, formGroup.value);
  }

  public buildAndRegisterColumnControl = (col: Column, form: UntypedFormGroup, data: DataObject): AbstractControl => {
    const { name } = col.formControlOptions;
    let control = form.get(name);
    if (!control) {
      control = this.makeColumnControl(col, data);
      form.registerControl(name, control);
    } else if (!control[_ExistingControlSymbol]) {
      control[_ExistingControlSymbol] = true;
    }
    return control;
  };

  public makeColumnControl(col: Column, data: DataObject): UntypedFormControl {
    const value = digger(data, col.getReadPath());
    return this.fb.control(value);
  }
}
